import type { TonyteActivity } from '../types';
import { getActivityImageCandidate } from './activityImages';

type TimestampLike = {
  seconds?: number;
  nanoseconds?: number;
  toDate?: () => Date;
};

type ActivityDateFields = TonyteActivity & {
  date?: unknown;
  startAt?: unknown;
  startsAt?: unknown;
  startDateTime?: unknown;
  endAt?: unknown;
  endsAt?: unknown;
  endDateTime?: unknown;
  timestamp?: unknown;
};

export type ParsedActivityDate = {
  startAt: Date | null;
  endAt: Date | null;
  dateKey: string | null;
  startTime: string | null;
  endTime: string | null;
};

const SIX_HOURS_MS = 6 * 60 * 60 * 1000;
const DEBUG_FEED_DATE_LOGS = false;

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

export function toLocalDateKey(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  next.setDate(next.getDate() + days);
  return next;
}

export function startOfLocalDay(date: Date): Date {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return start;
}

function parseTimestampLike(value: TimestampLike): Date | null {
  if (typeof value.toDate === 'function') {
    const date = value.toDate();
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value.seconds === 'number') {
    const date = new Date(value.seconds * 1000);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
}

function parseDateValue(value: unknown): Date | null {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'object') {
    return parseTimestampLike(value as TimestampLike);
  }

  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [year, month, day] = trimmed.split('-').map(Number);
    return new Date(year, month - 1, day, 12, 0, 0, 0);
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function parseTimeParts(value?: string): { hours: number; minutes: number } | null {
  if (!value) return null;
  const match = value.trim().match(/^(\d{1,2})(?::(\d{2}))?\s*([ap]m)?$/i);
  if (!match) return null;

  let hours = Number(match[1]);
  const minutes = Number(match[2] ?? '0');
  const suffix = match[3]?.toLowerCase();

  if (suffix === 'pm' && hours < 12) hours += 12;
  if (suffix === 'am' && hours === 12) hours = 0;
  if (hours > 23 || minutes > 59) return null;

  return { hours, minutes };
}

function applyTime(date: Date, time?: string): Date {
  const withTime = new Date(date);
  const parts = parseTimeParts(time);
  if (parts) {
    withTime.setHours(parts.hours, parts.minutes, 0, 0);
  } else {
    withTime.setHours(0, 0, 0, 0);
  }
  return withTime;
}

function endOfLocalDay(date: Date): Date {
  const end = new Date(date);
  end.setHours(23, 59, 59, 999);
  return end;
}

export function parseActivityDate(activity: TonyteActivity): ParsedActivityDate {
  const fields = activity as ActivityDateFields;
  const explicitStart =
    parseDateValue(fields.startAt) ??
    parseDateValue(fields.startsAt) ??
    parseDateValue(fields.startDateTime) ??
    parseDateValue(fields.timestamp);
  const explicitEnd =
    parseDateValue(fields.endAt) ??
    parseDateValue(fields.endsAt) ??
    parseDateValue(fields.endDateTime);

  const dateOnly =
    parseDateValue(activity.startDate) ??
    parseDateValue(fields.date) ??
    explicitStart;

  if (!dateOnly && !explicitStart) {
    return {
      startAt: null,
      endAt: null,
      dateKey: null,
      startTime: activity.startTime || null,
      endTime: activity.endTime || null,
    };
  }

  const startAt = explicitStart ?? applyTime(dateOnly!, activity.startTime);
  const parsedEndDate = parseDateValue(activity.endDate);
  const endDate = parsedEndDate ?? dateOnly ?? startAt;
  let endAt = explicitEnd ?? (
    activity.endTime
      ? applyTime(endDate, activity.endTime)
      : parsedEndDate
        ? endOfLocalDay(parsedEndDate)
        : null
  );

  if (!endAt) {
    endAt = activity.startTime
      ? new Date(startAt.getTime() + SIX_HOURS_MS)
      : endOfLocalDay(startAt);
  }

  if (endAt.getTime() < startAt.getTime()) {
    endAt = new Date(endAt);
    endAt.setDate(endAt.getDate() + 1);
  }

  return {
    startAt,
    endAt,
    dateKey: toLocalDateKey(startAt),
    startTime: activity.startTime || null,
    endTime: activity.endTime || null,
  };
}

export function isActivityCurrentOrUpcoming(
  activity: TonyteActivity,
  now = new Date(),
): boolean {
  const parsed = parseActivityDate(activity);
  if (!parsed.startAt || !parsed.endAt) return false;
  return parsed.endAt.getTime() >= now.getTime();
}

export function isEventExpired(event: TonyteActivity, now = new Date()): boolean {
  return !isActivityCurrentOrUpcoming(event, now);
}

export function isActivityVisibleForSelectedDate(
  activity: TonyteActivity,
  selectedDate: Date,
  now = new Date(),
): boolean {
  const parsed = parseActivityDate(activity);
  if (!parsed.startAt || !parsed.endAt) return false;

  const selectedKey = toLocalDateKey(selectedDate);
  const todayKey = toLocalDateKey(now);
  const eventOverlapsSelectedDate =
    toLocalDateKey(parsed.startAt) <= selectedKey &&
    toLocalDateKey(parsed.endAt) >= selectedKey;

  if (!eventOverlapsSelectedDate) return false;

  if (selectedKey === todayKey) {
    return parsed.endAt.getTime() >= now.getTime();
  }

  return true;
}

/** Returns the nearest future calendar day containing at least one visible
 * event. Used by Feed and Map when today's remaining event set is empty, so
 * the app advances to useful upcoming content instead of looking abandoned. */
export function findNextActivityDate(
  activities: TonyteActivity[],
  afterDate: Date,
  now = new Date(),
  maxDays = 30,
): Date | null {
  for (let offset = 1; offset <= maxDays; offset += 1) {
    const candidate = addDays(afterDate, offset);
    if (activities.some(activity => isActivityVisibleForSelectedDate(activity, candidate, now))) {
      return candidate;
    }
  }
  return null;
}

export function sortActivitiesByStart(a: TonyteActivity, b: TonyteActivity): number {
  const parsedA = parseActivityDate(a);
  const parsedB = parseActivityDate(b);
  const timeA = parsedA.startAt?.getTime() ?? Number.MAX_SAFE_INTEGER;
  const timeB = parsedB.startAt?.getTime() ?? Number.MAX_SAFE_INTEGER;
  return timeA - timeB || a.title.localeCompare(b.title);
}

/**
 * Events that began before the selected calendar day but still overlap it.
 * These remain eligible for the day, but belong in the compact "Continued
 * events" module rather than ahead of events whose actual start date is the
 * selected day. The comparison is date-key based so DST and local time zones
 * behave exactly like the feed's existing date filter.
 */
export function isActivityContinuedOnDate(activity: TonyteActivity, selectedDate: Date): boolean {
  const parsed = parseActivityDate(activity);
  if (!parsed.startAt || !parsed.endAt) return false;
  const selectedKey = toLocalDateKey(selectedDate);
  return toLocalDateKey(parsed.startAt) < selectedKey && toLocalDateKey(parsed.endAt) >= selectedKey;
}

export function partitionContinuedActivities(
  activities: TonyteActivity[],
  selectedDate: Date,
): { continued: TonyteActivity[]; startingOnSelectedDate: TonyteActivity[] } {
  const continued: TonyteActivity[] = [];
  const startingOnSelectedDate: TonyteActivity[] = [];
  activities.forEach(activity => {
    (isActivityContinuedOnDate(activity, selectedDate) ? continued : startingOnSelectedDate).push(activity);
  });
  return { continued, startingOnSelectedDate };
}

export function shouldDebugFeedDates(): boolean {
  return __DEV__ && DEBUG_FEED_DATE_LOGS;
}

export function debugActivityDateDecision(
  activity: TonyteActivity,
  selectedDate: Date,
  included: boolean,
  now = new Date(),
  reason = '',
) {
  if (!shouldDebugFeedDates()) return;
  const parsed = parseActivityDate(activity);
  console.log('[Tonyte][RN][FeedDate]', {
    currentDeviceDate: now.toISOString(),
    currentLocalDate: toLocalDateKey(now),
    currentLocalTime: now.toLocaleTimeString(),
    selectedFeedDate: toLocalDateKey(selectedDate),
    eventId: activity.id,
    title: activity.title,
    rawDate: (activity as ActivityDateFields).date ?? null,
    rawTimestamp: (activity as ActivityDateFields).timestamp ?? null,
    rawStartDate: activity.startDate,
    rawStartTime: activity.startTime,
    rawStartsAt: (activity as ActivityDateFields).startsAt ?? (activity as ActivityDateFields).startAt ?? null,
    rawEndDate: activity.endDate,
    rawEndTime: activity.endTime,
    rawEndsAt: (activity as ActivityDateFields).endsAt ?? (activity as ActivityDateFields).endAt ?? null,
    parsedStartAt: parsed.startAt?.toISOString() ?? null,
    parsedStartDate: parsed.startAt ? toLocalDateKey(parsed.startAt) : null,
    parsedStartTime: parsed.startAt?.toLocaleTimeString() ?? null,
    parsedEndAt: parsed.endAt?.toISOString() ?? null,
    parsedEndDate: parsed.endAt ? toLocalDateKey(parsed.endAt) : null,
    parsedEndTime: parsed.endAt?.toLocaleTimeString() ?? null,
    eventImageSource: getActivityImageCandidate(activity) || null,
    included,
    reason,
  });
}
